import {
  DayOfWeek,
} from 'dtos/dayOfWeek';
import keyBy from 'lodash/keyBy';
import keys from 'lodash/keys';
import mapValues from 'lodash/mapValues';
import values from 'lodash/values';
import {
  CommonOption,
} from 'pages/Dashboard/pages/Appointments/pages/List/types/appointment';
import {
  SocialHistory,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/SocialHistories/SocialHistoryInfo';

export type USStateType = {
  name: string;
  abbreviation: string | null;
};

export const US_STATES: USStateType[] = [
  // States
  { abbreviation: 'AL', name: 'Alabama' },
  { abbreviation: 'AK', name: 'Alaska' },
  { abbreviation: 'AZ', name: 'Arizona' },
  { abbreviation: 'AR', name: 'Arkansas' },
  { abbreviation: 'CA', name: 'California' },
  { abbreviation: 'CO', name: 'Colorado' },
  { abbreviation: 'CT', name: 'Connecticut' },
  { abbreviation: 'DE', name: 'Delaware' },
  { abbreviation: 'FL', name: 'Florida' },
  { abbreviation: 'GA', name: 'Georgia' },
  { abbreviation: 'HI', name: 'Hawaii' },
  { abbreviation: 'ID', name: 'Idaho' },
  { abbreviation: 'IL', name: 'Illinois' },
  { abbreviation: 'IN', name: 'Indiana' },
  { abbreviation: 'IA', name: 'Iowa' },
  { abbreviation: 'KS', name: 'Kansas' },
  { abbreviation: 'KY', name: 'Kentucky' },
  { abbreviation: 'LA', name: 'Louisiana' },
  { abbreviation: 'ME', name: 'Maine' },
  { abbreviation: 'MD', name: 'Maryland' },
  { abbreviation: 'MA', name: 'Massachusetts' },
  { abbreviation: 'MI', name: 'Michigan' },
  { abbreviation: 'MN', name: 'Minnesota' },
  { abbreviation: 'MS', name: 'Mississippi' },
  { abbreviation: 'MO', name: 'Missouri' },
  { abbreviation: 'MT', name: 'Montana' },
  { abbreviation: 'NE', name: 'Nebraska' },
  { abbreviation: 'NV', name: 'Nevada' },
  { abbreviation: 'NH', name: 'New Hampshire' },
  { abbreviation: 'NJ', name: 'New Jersey' },
  { abbreviation: 'NM', name: 'New Mexico' },
  { abbreviation: 'NY', name: 'New York' },
  { abbreviation: 'NC', name: 'North Carolina' },
  { abbreviation: 'ND', name: 'North Dakota' },
  { abbreviation: 'OH', name: 'Ohio' },
  { abbreviation: 'OK', name: 'Oklahoma' },
  { abbreviation: 'OR', name: 'Oregon' },
  { abbreviation: 'PA', name: 'Pennsylvania' },
  { abbreviation: 'RI', name: 'Rhode Island' },
  { abbreviation: 'SC', name: 'South Carolina' },
  { abbreviation: 'SD', name: 'South Dakota' },
  { abbreviation: 'TN', name: 'Tennessee' },
  { abbreviation: 'TX', name: 'Texas' },
  { abbreviation: 'UT', name: 'Utah' },
  { abbreviation: 'VT', name: 'Vermont' },
  { abbreviation: 'VA', name: 'Virginia' },
  { abbreviation: 'WA', name: 'Washington' },
  { abbreviation: 'WV', name: 'West Virginia' },
  { abbreviation: 'WI', name: 'Wisconsin' },
  { abbreviation: 'WY', name: 'Wyoming' },
  // Territories and Misc.
  { abbreviation: 'AS', name: 'American Samoa' },
  { abbreviation: 'DC', name: 'District Of Columbia' },
  { abbreviation: 'FM', name: 'Federated States of Micronesia' },
  { abbreviation: 'GU', name: 'Guam' },
  { abbreviation: 'MH', name: 'Marshall Islands' },
  { abbreviation: 'MP', name: 'Northern Mariana Islands' },
  { abbreviation: 'PR', name: 'Puerto Rico' },
  { abbreviation: 'PW', name: 'Palau' },
  { abbreviation: 'VI', name: 'Virgin Islands' },
  // Military
  { abbreviation: 'AA', name: 'Armed Forces' },
  { abbreviation: 'AE', name: 'Armed Forces' },
  { abbreviation: 'AP', name: 'Armed Forces' },
];

export const pageSizeOptions: number[] = [25, 50, 100];
export const weekDays: Record<DayOfWeek, string> = Object.freeze({
  Sunday: 'SU',
  Monday: 'MO',
  Tuesday: 'TU',
  Wednesday: 'WE',
  Thursday: 'TH',
  Friday: 'FR',
  Saturday: 'SA',
});

export const weekDaysValuesList = Object.freeze(keys(weekDays));
export const weekDaysLabelList = Object.freeze(values(weekDays));

export const daysOfWeek: CommonOption[] = weekDaysLabelList
  .map((day, index) => ({ label: day, id: index }));

export const SOCIAL_HISTORY_OPTIONS = {
  tobaccoUsedInLast12Months: {
    options: Object.freeze([
      { label: 'No', value: 'No' },
      { label: 'One to ten', value: '1 - 10 times' },
      { label: 'Eleven plus', value: '11+ times' },
    ]),
  },
  alcoholConsumedInLast12Months: {
    options: Object.freeze([
      { label: 'Zero to one', value: '0 - 1 times' },
      { label: 'Two to ten', value: '2 - 10 times' },
      { label: 'Eleven plus', value: '11+ times' },
    ]),
  },
  haveYouEverPutInAnAdvanceCarePlan: {
    options: Object.freeze([
      { label: 'Yes', value: 'Yes' },
      { label: 'No', value: 'No' },
      { label: 'Decline to answer', value: 'Decline to answer' },
    ]),
  },
  hadOneOrMoreMinorOrFallsInLast12Months: {
    options: Object.freeze([
      { label: 'Yes', value: 'Yes' },
      { label: 'No', value: 'No' },
    ]),
  },
};

export const optionsByCategory = mapValues(SOCIAL_HISTORY_OPTIONS, (category) => (
  keyBy(category.options, 'value')
));

const valuesByCategory = mapValues(SOCIAL_HISTORY_OPTIONS, (category) => (
  category.options.map((option) => option.value)
));

export const socialHistoryData: SocialHistory[] = [{
  id: 1,
  label: 'Used tobacco products in the last 12 months',
  key: 'tobaccoUsedInLast12Months',
  selectOptions: valuesByCategory.tobaccoUsedInLast12Months,
  chartDetailLabel: 'Tobacco',
}, {
  id: 2,
  label: 'Had more than 4-5 drinks in the last 12 months',
  key: 'alcoholConsumedInLast12Months',
  selectOptions: valuesByCategory.alcoholConsumedInLast12Months,
  chartDetailLabel: 'Social Drinking',
}, {
  id: 3,
  label: 'Some form of Advanced Care planning',
  key: 'haveYouEverPutInAnAdvanceCarePlan',
  selectOptions: valuesByCategory.haveYouEverPutInAnAdvanceCarePlan,
  chartDetailLabel: 'Advanced Care Plan',
}, {
  id: 4,
  label: '1+ major falls or 2+ minor falls in last 12 months',
  key: 'hadOneOrMoreMinorOrFallsInLast12Months',
  selectOptions: valuesByCategory.hadOneOrMoreMinorOrFallsInLast12Months,
  chartDetailLabel: 'Recent Falls',
}];

export const chartData = keyBy(socialHistoryData, 'key');

export const helpDeskURL = 'https://knowledge.populateemr.com';

export const maxBillingCodeSearchQueryLength = 128;

export const VisitNoteSOAPSectionNames = {
  AdditionalHPITemplate: 'AdditionalHPITemplate',
  PhysicalExamTemplate: 'PhysicalExamTemplate',
  PlanTemplate: 'PlanTemplate',
  RadiologyTemplate: 'RadiologyTemplate',
  AdditionalAssessmentTemplate: 'AdditionalAssessmentTemplate',
} as const;

export const DEFAULT_EMPTY_VALUE = '—';
export const DEFAULT_CONNECTOR = ' • ';